import { ArrowLeftRight, Unlink, Video } from 'lucide-react'
import { Avatar, Button, IconButton, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from '@/components/play'
import { formatAge } from '@/components/map/camera-sphere/formatAge'
import type { AthleteRecord, Camera, TrackerRecord } from '@/lib/athletes/types'

interface Props {
  trackers: readonly TrackerRecord[]
  athletes: ReadonlyMap<string, AthleteRecord>
  cameras: ReadonlyMap<string, Camera>
  /** Now, in epoch milliseconds: ages are read from it. */
  now: number
  onAssign: (tracker: TrackerRecord) => void
  onUnassign: (tracker: TrackerRecord) => void
}

/**
 * Every tracker and who carries it (docs/features/athletes-and-trackers.md, Host): an athlete by name and bib, a camera
 * by name, or no one yet. One with no one is assigned here; one that is carried is reassigned or unassigned.
 */
export function TrackersTable({ trackers, athletes, cameras, now, onAssign, onUnassign }: Props) {
  return (
    <Table caption="Trackers" hideCaption>
      <TableHeader>
        <TableRow>
          <TableHeaderCell type="data">Tracker</TableHeaderCell>
          <TableHeaderCell>Carried by</TableHeaderCell>
          <TableHeaderCell>Last seen</TableHeaderCell>
          <TableHeaderCell type="actions">
            <span className="sr-only">Actions</span>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {trackers.map((tracker) => {
          const athlete = tracker.athlete_id ? athletes.get(tracker.athlete_id) : undefined
          const camera = tracker.stream_id ? cameras.get(tracker.stream_id) : undefined
          const carried = Boolean(tracker.athlete_id || tracker.stream_id)
          return (
            <TableRow key={tracker.id}>
              <TableCell type="data" className="whitespace-nowrap">
                {tracker.external_ref}
                {tracker.label && <span className="block text-xs text-neutral-7">{tracker.label}</span>}
              </TableCell>
              <TableCell>
                {athlete ? (
                  <span className="flex items-center gap-2">
                    <Avatar name={athlete.name} src={athlete.photo_url} size="sm" tone="auto" toneKey={athlete.id} />
                    <span>
                      {athlete.name}
                      {athlete.bib && <span className="text-neutral-7"> · Bib {athlete.bib}</span>}
                    </span>
                  </span>
                ) : camera ? (
                  <span className="flex items-center gap-2">
                    <Video className="h-4 w-4 shrink-0" aria-hidden />
                    {camera.name}
                  </span>
                ) : carried ? (
                  <span className="text-neutral-7">Someone not on this list</span>
                ) : (
                  <span className="text-neutral-7">No one</span>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap">{tracker.location ? formatAge(now - Date.parse(tracker.location.recorded_at)) : 'Not yet'}</TableCell>
              <TableCell type="actions">
                {carried ? (
                  <>
                    <IconButton label={`Reassign ${tracker.external_ref}`} variant="ghost" onClick={() => onAssign(tracker)}>
                      <ArrowLeftRight />
                    </IconButton>
                    <IconButton label={`Unassign ${tracker.external_ref}`} variant="ghost" onClick={() => onUnassign(tracker)}>
                      <Unlink />
                    </IconButton>
                  </>
                ) : (
                  <Button variant="secondary" size="compact" onClick={() => onAssign(tracker)}>
                    Assign
                  </Button>
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
